import {
  AdapterWebItem as WebItem,
  PrasedCategoryLinkType,
  SelectedNode,
} from './types';

const extractURLHashValue = (urlString: string) => {
  const hashSign = '#';
  const hashValue = urlString.split(hashSign)[1];
  if (hashValue) {
    return hashSign + hashValue;
  }
  return '';
};

const parseCategoryLink = (category: WebItem): PrasedCategoryLinkType => {
  const { link, customUrl } = category;
  const preferredUrl = customUrl || link;
  const hash = extractURLHashValue(preferredUrl);
  const urlWithoutHash = preferredUrl.split('#')[0];
  const parameterDivider = preferredUrl.indexOf('?') > -1 ? '&' : '?';
  const trackingParam = 'cl=true';

  return {
    ...category,
    link: `${urlWithoutHash}${parameterDivider}${trackingParam}${hash}`,
  };
};

const combineCategoryGroups = (groups: WebItem[]): WebItem[] =>
  groups.reduce(
    (allCategories: WebItem[], group) => allCategories.concat(group.children),
    []
  );

const isHiddenCategoryOrSameAsCurrentCategory = ({
  category,
  currentCategory,
}: {
  category: WebItem;
  currentCategory: string | undefined;
}) => {
  return category.hidden || category.id === currentCategory;
};

const filterCategory = ({
  category,
  currentCategory,
}: {
  category: WebItem;
  currentCategory: string | undefined;
}): boolean =>
  !(
    isHiddenCategoryOrSameAsCurrentCategory({ category, currentCategory }) ||
    category.type === 'spacer' ||
    category.name.toLowerCase().indexOf('factory') !== -1
  );

const findDivisionCategoryGroups = ({
  divisionId,
  webHierarchy,
}: {
  divisionId: string | undefined;
  webHierarchy: WebItem[];
}): WebItem[] => {
  const matchesDivisionId = (division: WebItem) =>
    division.id === divisionId ? division.children : [];

  return webHierarchy
    .map((division) => {
      if (division.hasSubDivision) {
        return division.children
          .map(matchesDivisionId)
          .filter((bool) => bool)
          .reduce((a, b) => a.concat(b), []);
      }

      return matchesDivisionId(division);
    })
    .filter((bool) => bool)
    .reduce((a, b) => a.concat(b), []);
};

const getCurrentCategoryId = (selectedNodes: SelectedNode[]) =>
  selectedNodes.find(({ type }) => type === 'category' || type === 'sale') ||
  {};

const getDivisionId = (selectedNodes: SelectedNode[]) =>
  selectedNodes.find(
    ({ type }) => type === 'division' || type === 'sub-division'
  ) || {};

const categoriesBasedOnHierarchyAndNodes = (
  webHierarchy: WebItem[],
  selectedNodes: SelectedNode[]
): {
  currentCategory: string | undefined;
  combinedCategoryGroups: WebItem[];
} => {
  const { id: currentCategory }: { id?: string } =
    getCurrentCategoryId(selectedNodes);
  const { id: did }: { id?: string } = getDivisionId(selectedNodes);

  const groups = findDivisionCategoryGroups({ divisionId: did, webHierarchy });
  const combinedCategoryGroups = combineCategoryGroups(groups);

  return { currentCategory, combinedCategoryGroups };
};

const getRelatedCategories = ({
  webHierarchy,
  selectedNodes,
}: {
  webHierarchy: WebItem[];
  selectedNodes: SelectedNode[];
}): WebItem[] => {
  const { currentCategory, combinedCategoryGroups } =
    categoriesBasedOnHierarchyAndNodes(webHierarchy, selectedNodes);
  return combinedCategoryGroups
    .filter((category) => filterCategory({ category, currentCategory }))
    .map((category) => parseCategoryLink(category));
};

const getCurrentCategory = ({
  webHierarchy,
  selectedNodes,
}: {
  webHierarchy: WebItem[];
  selectedNodes: SelectedNode[];
}): WebItem | undefined => {
  const { currentCategory, combinedCategoryGroups } =
    categoriesBasedOnHierarchyAndNodes(webHierarchy, selectedNodes);
  return combinedCategoryGroups
    .filter((category) => category.id === currentCategory)
    .shift();
};

export {
  parseCategoryLink,
  combineCategoryGroups,
  filterCategory,
  findDivisionCategoryGroups,
  getRelatedCategories,
  getCurrentCategory,
};
